import React from "react";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

/**
 * Predefined colors for AAC boards.
 * Each color maps to a CSS variable (--design-rowN-*) so the Design Control Center
 * can override colors in real-time for both Light and Dark modes.
 * 
 * Color-to-row mapping:
 *   purple → Row 1, teal → Row 2, green → Row 3, gray → Row 4, yellow → Row 5
 */
export const boardColors = [
  { id: "teal",   name: "טורקיז", row: 2, bg: "bg-[hsl(var(--design-row2-bg))]", border: "border-[hsl(var(--design-row2-border))]", text: "text-[hsl(var(--design-row2-text))]", icon: "text-[hsl(var(--design-row2-icon))]" },
  { id: "orange", name: "כתום",   row: 0, bg: "bg-[hsl(25_80%_92%)]", border: "border-[hsl(25_60%_72%)]", text: "text-[#1A2E35]", icon: "text-[#1A2E35]" },
  { id: "blue",   name: "כחול",   row: 0, bg: "bg-[hsl(220_50%_93%)]", border: "border-[hsl(220_45%_75%)]", text: "text-[#1A2E35]", icon: "text-[#1A2E35]" },
  { id: "green",  name: "ירוק",   row: 3, bg: "bg-[hsl(var(--design-row3-bg))]", border: "border-[hsl(var(--design-row3-border))]", text: "text-[hsl(var(--design-row3-text))]", icon: "text-[hsl(var(--design-row3-icon))]" },
  { id: "purple", name: "סגול",   row: 1, bg: "bg-[hsl(var(--design-row1-bg))]", border: "border-[hsl(var(--design-row1-border))]", text: "text-[hsl(var(--design-row1-text))]", icon: "text-[hsl(var(--design-row1-icon))]" },
  { id: "pink",   name: "ורוד",   row: 0, bg: "bg-[hsl(340_45%_93%)]", border: "border-[hsl(340_40%_75%)]", text: "text-[#1A2E35]", icon: "text-[#1A2E35]" },
  { id: "yellow", name: "צהוב",   row: 5, bg: "bg-[hsl(var(--design-row5-bg))]", border: "border-[hsl(var(--design-row5-border))]", text: "text-[hsl(var(--design-row5-text))]", icon: "text-[hsl(var(--design-row5-icon))]" },
  { id: "red",    name: "אדום",   row: 0, bg: "bg-[hsl(0_50%_93%)]", border: "border-[hsl(0_45%_72%)]", text: "text-[#1A2E35]", icon: "text-[#1A2E35]" },
  { id: "gray",   name: "אפור",   row: 4, bg: "bg-[hsl(var(--design-row4-bg))]", border: "border-[hsl(var(--design-row4-border))]", text: "text-[hsl(var(--design-row4-text))]", icon: "text-[hsl(var(--design-row4-icon))]" },
  { id: "indigo", name: "אינדיגו", row: 0, bg: "bg-[hsl(235_40%_93%)]", border: "border-[hsl(235_35%_75%)]", text: "text-[#1A2E35]", icon: "text-[#1A2E35]" },
];

export type BoardColorId = typeof boardColors[number]["id"];

export function getColorClasses(colorId: string) {
  return boardColors.find(c => c.id === colorId) || boardColors[0];
}

interface ColorPickerProps {
  selectedColor?: string;
  onSelect: (colorId: string) => void;
  onColorSelect?: (colorId: string) => void; // Alias for consistency
}

export function ColorPicker({ selectedColor, onSelect, onColorSelect }: ColorPickerProps) {
  const handleSelect = (colorId: string) => {
    onSelect?.(colorId);
    onColorSelect?.(colorId);
  };

  return (
    <div className="flex flex-wrap gap-3">
      {boardColors.map((color) => (
        <button
          key={color.id}
          onClick={() => handleSelect(color.id)}
          className={cn(
            "w-12 h-12 rounded-xl border-2 transition-all flex items-center justify-center",
            "hover:scale-110",
            color.bg,
            color.border,
            selectedColor === color.id && "ring-2 ring-offset-2 ring-primary"
          )}
          title={color.name}
        >
          {selectedColor === color.id && (
            <Check className={cn("w-5 h-5", color.text)} />
          )}
        </button>
      ))}
    </div>
  );
}
